// Static, human-curated content for the Resources tab.
//
// Keys MUST match the exact goalCategory strings stored in Firestore
// (see the categories list in OnboardingPage.js). One wrong character
// and that category shows the "no match" message instead of its content.
//
// Shape of each category:
//   intro:    one or two sentences shown at the top
//   articles: [{ id, title, body: [paragraph, paragraph, ...] }]
//   links:    [{ id, title, url, source, note }]
//
// Translations: the English text below is the fallback. The translated
// versions live in src/i18n/translations.js under keys built from the ids,
// for example res_fitness_showing_up_p1. Keep the two in step.
//
// Rules: every url must be https and must have been opened and checked
// by a human before it is added here. Never add a link you have not clicked.

const RESOURCES = {
  'Fitness & Health': {
    intro:
      'Fitness and health goals tend to be won in small, ordinary moments, not big ones. These pieces are here to take some pressure off, and the links lead to sources that are good at the detail.',
    articles: [
      {
        id: 'fitness-showing-up',
        title: 'Showing up beats showing off',
        body: [
          'Most of us carry a picture of what “proper” exercise looks like: the gym, the running shoes, the sweat. When life doesn’t fit that picture, it’s easy to decide the whole thing isn’t for you.',
          'The World Health Organization takes a much wider view. It counts movement during leisure time, getting from place to place, and the activity in your work or around the home. Walking, cycling, sport and play all count, at any level of skill. Its guidelines make a point of saying that any amount of activity is better than none.',
          'That matters when you’re building a habit with a partner. A short walk you actually did is worth more than the hard session you planned and skipped. The check-in that says “I only managed ten minutes” is still a check-in that kept the thread going.',
          'It also helps to separate what you did from how it felt. Some days the habit is the whole win. Intensity can come later, once showing up has stopped being a negotiation.',
          'If you have a health condition, are pregnant, or haven’t been active in a long time, it’s worth a quick conversation with a doctor or another qualified health professional before changing how hard you train.',
        ],
      },
      {
        id: 'fitness-rest',
        title: 'Rest is part of the plan',
        body: [
          'When people talk about fitness goals, sleep and rest usually get treated like the stuff left over once the real work is done. In practice they’re a big part of whether the work holds up.',
          'Sleep and activity feed each other. The NHS notes that regular activity can help you sleep better, and that some people find exercising close to bedtime gets in the way, in which case earlier in the day may suit them. Neither is a rule. They’re a reminder that you’re the one who gets to find out what works for your body.',
          'Rest is also worth planning for when you’re keeping each other accountable. If every check-in becomes a report on how much you pushed, a quiet day starts to feel like a failure. A day off that you chose on purpose is different from drifting away from the goal, and it’s fine to say so out loud.',
          'If sleep is a real struggle over a long stretch, or you’re feeling worn down for reasons you can’t pin down, that’s worth raising with a doctor or another qualified health professional. It’s not something to push through alone.',
        ],
      },
      {
        id: 'fitness-food',
        title: 'Food without the fuss',
        body: [
          'Food advice is loud and often contradictory, so it’s worth knowing that the big public health bodies are more relaxed than most of the internet.',
          'The WHO says healthy diets come in many forms. What counts as a sensible way of eating depends on your age, lifestyle, culture, and the foods you can actually get where you live. It describes four ideas that sit underneath any of them: getting enough of what your body needs, keeping things in balance, enjoying some foods in moderation, and eating a wide variety. A mostly unprocessed, varied diet is the foundation it points to.',
          'For a goal you’re pursuing with someone else, that’s a useful frame. You don’t need to copy your partner’s way of eating, and they don’t need to copy yours. A food goal can be as simple as “cook at home more often” or “eat something other than the usual thing this week”, whatever fits your life and budget.',
          'If you’re thinking about big changes to how you eat, or you have a condition, allergy or medication that food affects, a doctor or registered dietitian can give advice that fits you. A general write-up can’t.',
        ],
      },
    ],
    links: [
      {
        id: 'fitness-who-physical-activity',
        title: 'Physical activity',
        url: 'https://www.who.int/news-room/fact-sheets/detail/physical-activity',
        source: 'World Health Organization',
        note: 'Global. A plain overview of what counts as activity, who benefits and why.',
      },
      {
        id: 'fitness-who-guidelines',
        title: 'WHO guidelines on physical activity and sedentary behaviour',
        url: 'https://www.who.int/publications/i/item/9789240015128',
        source: 'World Health Organization',
        note: 'Global. The full guidelines, with recommendations for different ages and for people living with chronic conditions or disability.',
      },
      {
        id: 'fitness-who-healthy-diet',
        title: 'Healthy diet',
        url: 'https://www.who.int/news-room/fact-sheets/detail/healthy-diet',
        source: 'World Health Organization',
        note: 'Global. Explains the principles behind a healthy diet and why it varies by culture and local food.',
      },
      {
        id: 'fitness-nhs-sleep',
        title: 'How to fall asleep faster and sleep better',
        url: 'https://www.nhs.uk/every-mind-matters/mental-wellbeing-tips/how-to-fall-asleep-faster-and-sleep-better/',
        source: 'NHS Every Mind Matters (UK)',
        note: 'UK. Practical sleep advice. Its mentions of a GP or pharmacist refer to UK services.',
      },
    ],
  },

  'Learning & Skills': {
    intro:
      'Learning something new mostly comes down to showing up often enough for it to stick. These pieces are about the long game, and the links are free places to learn almost anything.',
    articles: [
      {
        id: 'learning-small-repeat',
        title: 'Small enough to repeat',
        body: [
          'Most learning goals die from being too big, not too hard. “Learn to code” or “become fluent in Portuguese” is a destination, not something you can do on a Tuesday evening.',
          'What you can do on a Tuesday evening is sit down for twenty minutes with one lesson. That’s a task you can repeat, and repeating it is where the learning builds up. Skills tend to arrive quietly, a little at a time, until one day you notice you can do something you couldn’t before.',
          'This is where an accountability partner helps more than motivation does. Motivation comes and goes. A partner who’s expecting to hear how it went makes it harder to quietly drop the habit and easier to restart after a bad week.',
          'A good check-in doesn’t have to be impressive. “I did one lesson and understood about half” is honest, and it keeps the habit alive. You can raise the size of the task later. It’s much harder to rebuild a habit that has stopped.',
        ],
      },
      {
        id: 'learning-beginner',
        title: 'Being a beginner is allowed',
        body: [
          'Starting something new means being bad at it, at least in front of one person. For many adults that’s the real obstacle, more than time or money.',
          'It may help to know how open learning has become. MIT OpenCourseWare publishes materials from thousands of MIT courses for free, with no registration. The Open University’s OpenLearn offers nearly a thousand short free courses, and says there are no requirements to access them. You don’t need anyone’s permission to try a subject, or to find out it isn’t for you.',
          'Trying something and deciding it’s not for you counts as learning too. Sampling a few things before committing to one is a perfectly good way to start.',
          'When you feel behind, remember that your partner may well feel the same about their own goal. You’re not competing. You’re both near the start of something, in different places.',
        ],
      },
      {
        id: 'learning-choose-one',
        title: 'Choose one thing, then keep choosing it',
        body: [
          'Free learning has a strange problem: there’s too much of it. OpenCourseWare alone covers more than 2,500 courses, and OpenLearn has nearly a thousand. It’s easy to spend a whole evening browsing and call it learning.',
          'It helps to pick one course, one book or one series of lessons, and treat everything else as “for later”. Saving the others in a note is fine. The aim is to finish one thing before starting the next. Telling your partner which one you picked is a small commitment that makes it easier to stick with.',
          'It’s also worth checking what a resource does and doesn’t give you. OpenLearn courses, for example, come with a statement of participation, and some with a digital badge that isn’t an accredited qualification. If you need a formal qualification for work or study, it’s worth knowing that early, and the institution you’re aiming for can tell you what they accept.',
        ],
      },
    ],
    links: [
      {
        id: 'learning-mit-ocw',
        title: 'MIT OpenCourseWare',
        url: 'https://ocw.mit.edu/',
        source: 'MIT',
        note: 'Global. Free lecture notes, exams and videos from thousands of MIT courses, with no registration. In English.',
      },
      {
        id: 'learning-khan-academy',
        title: 'Khan Academy',
        url: 'https://www.khanacademy.org/',
        source: 'Khan Academy (nonprofit)',
        note: 'Global. A nonprofit offering free lessons and practice, strongest in maths and science, in many languages.',
      },
      {
        id: 'learning-openlearn',
        title: 'OpenLearn free courses',
        url: 'https://www.open.edu/openlearn/free-courses',
        source: 'The Open University (UK)',
        note: 'UK-based, open to anyone. Nearly 1,000 free short courses, from about 1 to 24 hours, each with a free statement of participation.',
      },
    ],
  },

  'Business & Career': {
    intro:
      'Work goals are long, and most of the effort happens in the days between milestones. These pieces are about keeping a steady pace, and the links are free places to build skills and plan your next step.',
    articles: [
      {
        id: 'business-progress',
        title: 'Progress at work is rarely a straight line',
        body: [
          'Career and business goals have a way of stretching out. A promotion, a career change or a first customer can take months, and the days in between rarely feel like progress.',
          'It helps to separate the outcome you want from the things you can actually do this week. You can’t control whether a company hires you, or whether a customer says yes. You can control how many conversations you have, how much time you give the project, and whether you follow up on the thing you said you would.',
          'That’s where an accountability partner earns their place. They don’t need to understand your industry. They just need to ask what you did, and listen to the answer without making it a performance.',
          'Some weeks the honest report is “not much happened”. It’s worth saying out loud, because a goal that goes quiet for a month is much harder to pick up again than one you kept lightly alive.',
        ],
      },
      {
        id: 'business-start-from-you',
        title: 'Start from you, not the job',
        body: [
          'When people feel stuck at work, the instinct is often to start with the job market: what’s hiring, what pays well, what looks impressive. It can be more useful to start somewhere else.',
          'OpenLearn’s free course Succeed in the workplace is built on that idea. It begins with the view that getting to know yourself and what you value is the foundation of career planning, and only then moves on to CVs, applications and interviews.',
          'In plain terms: what do you want your days to feel like? What are you good at that you rarely get credit for? What would you refuse to trade away, whether that’s time with family, steady income or room to be creative?',
          'You don’t have to answer all of that alone. Talking it through with your partner often brings up things you’d skip over by yourself, and it can turn a vague “I want something different” into a goal you can actually work on.',
        ],
      },
      {
        id: 'business-wear-out',
        title: 'Working hard without wearing yourself out',
        body: [
          'Ambition and exhaustion can look alike from the inside, especially when a goal matters to you.',
          'The World Health Organization notes that work can protect mental health, and that it can also contribute to harm. Its guidelines on mental health at work point to risks such as heavy workloads and negative behaviours at work, and they say the responsibility for a healthy workplace sits with employers and governments, not only with individual workers.',
          'If you’re checking in with a partner about a big work goal, it’s worth asking not only “did you get it done?” but “how are you holding up?” A goal pursued at the cost of your sleep, health or relationships is worth rethinking, even if it’s going well on paper.',
          'If work is affecting your health, or you’re feeling persistently low, anxious or worn out, please talk to a doctor or another qualified health professional. If you’re unsure of your rights at work, a trusted local source, such as a labour authority or a workers’ organisation, can explain them for where you live. Rules differ a lot between countries.',
        ],
      },
    ],
    links: [
      {
        id: 'business-who-mh-work',
        title: 'Guidelines on mental health at work',
        url: 'https://www.who.int/publications/i/item/9789240053052',
        source: 'World Health Organization',
        note: 'Global. Written mainly for employers and governments, so it’s technical. The executive summary is available in Arabic, Chinese, English, French, Russian and Spanish.',
      },
      {
        id: 'business-openlearn-entrepreneurship',
        title: 'Entrepreneurship – from ideas to reality',
        url: 'https://www.open.edu/openlearn/money-business/entrepreneurship-ideas-reality/',
        source: 'OpenLearn, The Open University (UK)',
        note: 'UK-based course. 24 hours across 8 sessions, and you can start without signing up. Some of it covers UK business rules, so check what applies in your country.',
      },
      {
        id: 'business-openlearn-succeed',
        title: 'Succeed in the workplace',
        url: 'https://www.open.edu/openlearn/education-development/succeed-the-workplace',
        source: 'OpenLearn, The Open University (UK)',
        note: 'About 8 weeks at around 3 hours a week, covering career planning, CVs and interviews. Last updated in 2021, so some parts on job searching and social media may feel dated.',
      },
    ],
  },

  'Creative Projects': {
    intro:
      'Creative projects tend to live in the gaps between everything else. These pieces are about keeping something alive through the long middle, and the links cover learning to write and using and sharing creative work.',
    articles: [
      {
        id: 'creative-finishing',
        title: 'Finishing is a skill too',
        body: [
          'Starting a creative project is the easy part. The idea is fresh and everything feels possible. The hard part comes in the middle, when the idea has turned into actual work and the work is slower and messier than the idea was.',
          'That’s usually where projects stall, and it’s rarely because the person lacks talent. It’s more often because the middle has no finish line to look forward to and nobody is watching.',
          'This is where a partner who simply asks “what did you make this week?” can make a real difference. You don’t need them to judge it. Knowing that you’ll be describing your week gives the work a small deadline, and a small deadline is often enough to get you back to the desk, the canvas or the instrument.',
          'It also helps to decide what “done” means for the thing in front of you. A finished rough version counts for more than a perfect one that stays in your head. You can always make it better after it exists.',
        ],
      },
      {
        id: 'creative-rough-drafts',
        title: 'Nobody needs to see the first version',
        body: [
          'Many creative people quietly stop because the first attempt isn’t as good as the picture in their mind. That gap between what you imagine and what you can make right now is normal. It’s often biggest when you care the most.',
          'Showing rough work to one trusted person can take some of the weight off. Telling a partner “this is a messy draft, but here’s what I’m trying to do” turns it into a conversation, not a verdict. You may find they care more about whether you kept going than whether it’s good.',
          'If you’re a beginner in a craft, there are gentle ways in. OpenLearn, the Open University’s free learning site, has an introductory course on writing fiction aimed at people just starting out. It’s one example among many, and it suits writers more than other kinds of makers.',
          'Progress in creative work rarely looks like steady improvement. Some weeks you make something you’re proud of, and some weeks you make something you’ll never show anyone. Both count, and both are part of the same habit.',
        ],
      },
      {
        id: 'creative-using-sharing',
        title: 'Using other people’s work, and sharing your own',
        body: [
          'Creative projects often involve other people’s material, such as images, sounds or text, and eventually you may want to share your own. A little background helps with both.',
          'Creative Commons is a nonprofit that publishes licences letting creators keep their copyright while allowing others to use their work in ways the creator chooses. If you want to share something you’ve made, its guidance explains how those licences work.',
          'Some institutions go further. The Smithsonian has released millions of images and data under what’s called CC0, which means you can use them without asking the Smithsonian’s permission, including for creative projects. But its own FAQ points out that CC0 only covers copyright. Other rights, such as privacy or trademark, may still apply, and some items in its collections aren’t included at all.',
          'Copyright rules differ between countries, and this is general information, not legal advice. If a project matters commercially, or you’re unsure whether you can use something, a qualified professional can tell you where you stand.',
        ],
      },
    ],
    links: [
      {
        id: 'creative-openlearn-fiction',
        title: 'Start writing fiction: characters and stories',
        url: 'https://www.open.edu/openlearn/history-the-arts/start-writing-fiction-characters-and-stories',
        source: 'OpenLearn, The Open University (UK)',
        note: 'UK-based, free to start. An introductory course on writing fiction, with a free statement of participation. It suits writers more than other kinds of makers.',
      },
      {
        id: 'creative-cc-license',
        title: 'CC License Your Work',
        url: 'https://creativecommons.org/cc-license-your-work/',
        source: 'Creative Commons',
        note: 'Global. How to let others use your work on terms you choose while keeping your copyright. General information, not legal advice.',
      },
      {
        id: 'creative-smithsonian-open-access',
        title: 'Smithsonian Open Access FAQ',
        url: 'https://www.si.edu/node/631369',
        source: 'Smithsonian Institution (US)',
        note: 'US institution, free for anyone to use. Explains the images and data released as CC0, what that does and doesn’t allow, and which items are excluded.',
      },
    ],
  },

  'Relationships & Connection': {
    intro:
      'Relationships are built more from small, repeated things than from big moments. These pieces are about keeping connections alive, what research says matters most, and what to do when connection is hard or a relationship is unsafe.',
    articles: [
      {
        id: 'relationships-small-gestures',
        title: 'Small gestures count',
        body: [
          'Many of us treat friendships and family ties as things that either exist or don’t. Robert Waldinger, who directs the Harvard Study of Adult Development, describes social life as a living system that needs maintenance, much like physical fitness.',
          'The maintenance can be tiny. Think of someone you miss and send them a message. The World Health Organization’s list of everyday steps is just as modest: reaching out to a friend, putting your phone away to be properly present in a conversation, greeting a neighbour, joining a local group, or volunteering.',
          'For a goal you share with an accountability partner, this makes a good weekly question: who did you reach out to? It’s concrete, it’s small, and you can answer it honestly even in a hard week.',
          'Not every message gets a warm reply, and that’s part of it. Waldinger says people are often surprised by how warmly small messages like these are received.',
        ],
      },
      {
        id: 'relationships-quality',
        title: 'Quality over quantity',
        body: [
          'It’s easy to measure a social life by headcount: how many friends, how many events, whether you’re in a relationship. The research points somewhere else.',
          'The Harvard Study of Adult Development has followed people since 1938. When its researchers looked back at participants’ lives in middle age, satisfaction in their relationships, particularly marriages, predicted who would be happy and healthy at 80 better than things like cholesterol or blood pressure did. Its director says other research groups have since found the same thing. He also says there’s no set number of connections you need, and that shy people may need fewer than extroverts.',
          'One caution: the study’s first participants were Harvard students and young adults from one American city, so it isn’t a picture of everyone. It’s worth holding the conclusions loosely.',
          'What it suggests, gently, is that at least one solid relationship, someone you could call if you were sick or scared, matters more than a big circle. And it’s rarely too late. The study’s director describes people who found close friendships or love late in life when they didn’t expect to.',
        ],
      },
      {
        id: 'relationships-hard-times',
        title: 'When connection is hard, and when a relationship hurts',
        body: [
          'Feeling lonely doesn’t mean something is wrong with you. The World Health Organization estimates that about one in six people worldwide experience loneliness, at every age. It describes loneliness as the painful feeling that comes when the connections you have don’t match the ones you want.',
          'The causes can include poor health, low income, living alone, or the way we use technology. The WHO notes that if the problem is more serious, finding out what support and services exist is worth doing.',
          'There’s another side: relationships that hurt. Harmful behaviour in a partnership includes psychological abuse and controlling behaviour, as well as physical or sexual harm.',
          'If you’re afraid of someone close to you, or you’re being hurt, please contact your local emergency number or a local helpline or support service. The right numbers depend on your country, and a Veyl partner is a peer, not a trained counsellor. If things feel too heavy to carry, a doctor or counsellor can help.',
        ],
      },
    ],
    links: [
      {
        id: 'relationships-who-social-connection',
        title: 'From loneliness to social connection: plain language summary',
        url: 'https://www.who.int/publications/m/item/from-loneliness-to-social-connection-summary',
        source: 'World Health Organization',
        note: 'Global. A short, jargon-free summary of WHO’s 2025 report on loneliness and social connection (13 pages).',
      },
      {
        id: 'relationships-harvard-gazette',
        title: 'Relationships make us happy — and healthy',
        url: 'https://news.harvard.edu/gazette/story/2023/02/work-out-daily-ok-but-how-socially-fit-are-you/',
        source: 'Harvard Gazette (US)',
        note: 'US source. A Q&A with the director of the Harvard Study of Adult Development on what the research says about relationships and health, including small everyday steps. It’s an interview, and the study’s early participants came from one American city.',
      },
      {
        id: 'relationships-who-violence',
        title: 'Violence against women',
        url: 'https://www.who.int/news-room/fact-sheets/detail/violence-against-women',
        source: 'World Health Organization',
        note: 'Global. Explains what intimate partner violence includes and how common it is, mostly affecting women. It’s information, not a helpline, and it can be hard to read.',
      },
    ],
  },

  // Placeholders: these show a friendly "coming soon" message until filled in.
  'Home & Life Organization':   { intro: '', articles: [], links: [] },
  'Money & Financial Habits':   { intro: '', articles: [], links: [] },
  'Staying Connected':          { intro: '', articles: [], links: [] },
  'Independent Living':         { intro: '', articles: [], links: [] },
  'Tech Confidence':            { intro: '', articles: [], links: [] },
};

export default RESOURCES;